// // src/AuthContext.js
// import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
// import { onAuthStateChanged } from "firebase/auth";
// import { auth, db } from "../firebase";
// import { doc, getDoc } from "firebase/firestore";

// const AuthCtx = createContext(null);

// export function AuthProvider({ children }) {
//   const [currentUser, setCurrentUser] = useState(null);
//   const [userDoc, setUserDoc] = useState(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     const unsub = onAuthStateChanged(auth, async (user) => {
//       setCurrentUser(user);
//       if (!user) {
//         setUserDoc(null);
//         setLoading(false);
//         return;
//       }
//       try {
//         const ref = doc(db, "users", user.uid);
//         const snap = await getDoc(ref);
//         setUserDoc(snap.exists() ? snap.data() : null);
//       } finally {
//         setLoading(false);
//       }
//     });
//     return () => unsub();
//   }, []);

//   const value = useMemo(() => {
//     const role = userDoc?.role ?? null;
//     const communities = userDoc?.communities ?? [];
//     const primaryCommunityId = userDoc?.primaryCommunityId ?? communities[0] ?? null;

//     return {
//       loading,
//       currentUser,
//       role,
//       isAdmin: role === "admin",
//       isManager: role === "manager",
//       isResident: role === "resident",
//       communities,
//       primaryCommunityId,
//       userDoc,
//     };
//   }, [loading, currentUser, userDoc]);

//   return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
// }

// export function useAuth() {
//   return useContext(AuthCtx);
// }

// src/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubUserDoc = null;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Cleanup any previous userDoc listener
      if (typeof unsubUserDoc === "function") {
        unsubUserDoc();
        unsubUserDoc = null;
      }

      if (!user) {
        setUserDoc(null);
        setLoading(false);
        return;
      }

      try {
        // Real-time stream of the user's profile
        const ref = doc(db, "users", user.uid);
        unsubUserDoc = onSnapshot(
          ref,
          (snap) => {
            setUserDoc(snap.exists() ? snap.data() : null);
            setLoading(false);
          },
          async (err) => {
            console.error("user doc onSnapshot error:", err);
            // Fallback to a one-time get if snapshot fails
            try {
              const once = await getDoc(ref);
              setUserDoc(once.exists() ? once.data() : null);
            } catch (e) {
              console.error("user doc getDoc fallback error:", e);
            } finally {
              setLoading(false);
            }
          }
        );
      } catch (e) {
        console.error("user doc subscription setup failed:", e);
        // Final fallback: single get
        try {
          const ref = doc(db, "users", user.uid);
          const snap = await getDoc(ref);
          setUserDoc(snap.exists() ? snap.data() : null);
        } finally {
          setLoading(false);
        }
      }
    });

    return () => {
      if (typeof unsubUserDoc === "function") unsubUserDoc();
      unsubAuth();
    };
  }, []);

  const value = useMemo(() => {
    const rawRole = userDoc?.role ?? null;
    const role = typeof rawRole === "string" ? rawRole.toLowerCase() : null;

    const communities = Array.isArray(userDoc?.communities) ? userDoc.communities : [];
    const primaryCommunityId = userDoc?.primaryCommunityId ?? communities[0] ?? null;

    const isAdmin = role === "admin";
    const isManager = role === "manager";
    const isResident = role === "resident";
    const isSecurity = role === "security";

    const hasRole = (...roles) => roles.map(r => String(r).toLowerCase()).includes(role);

    return {
      loading,
      currentUser,
      userDoc,                 // raw user profile (e.g., fullName, email, phone, staffType, etc.)
      role,                    // normalized
      isAdmin,
      isManager,
      isResident,
      isSecurity,
      hasRole,                 // helper e.g., hasRole('admin','manager')
      communities,
      primaryCommunityId,
    };
  }, [loading, currentUser, userDoc]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}